import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import authService from '../api/authService';
import config from '../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faSave, 
  faLock, 
  faSignOutAlt, 
  faCheck,
  faUser,
  faCog,
  faBuilding,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { AdminPageLayout } from './common';
import '../styles/AdminProfile.css';

const AdminProfilePage = () => {
  const location = useLocation();
  const { currentUser, logout, refreshUserProfile } = useAuth();
  const { adminGroup, loading: groupLoading, updateAdminGroup } = useGroup();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(currentUser || {});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: true,
    userName: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('account'); // Default to Account tab
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const fileInputRef = useRef(null);

  // Initialize form data when adminGroup is loaded
  useEffect(() => {
    if (adminGroup) {
      setFormData(prevState => ({
        ...prevState,
        name: adminGroup.name || '',
        description: adminGroup.description || '',
        isPublic: adminGroup.is_public !== false
      }));
    }
  }, [adminGroup]);

  // Initialize user data when currentUser is loaded
  useEffect(() => {
    if (currentUser) {
      setFormData(prevState => ({
        ...prevState,
        userName: currentUser.name || ''
      }));
      setEditedName(currentUser.name || '');
    }
    
    if (adminGroup && adminGroup.logo_url) {
      setLogoPreview(adminGroup.logo_url);
    }
  }, [currentUser, adminGroup]);

  // Initialize profile when currentUser changes
  useEffect(() => {
    if (currentUser) {
      setProfile(currentUser);
    }
  }, [currentUser]);

  // Handle URL parameters for tab switching
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['account'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Fetch latest profile data from database
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${config.API_URL}/users/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setProfile(prevProfile => ({
              ...prevProfile,
              ...result.data,
              stripe_account_id: result.data.stripe_account_id
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setFormData(prevState => ({
      ...prevState,
      [name]: inputValue
    }));
  };

  // Handle logo file input change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger the file input click when clicking on the logo area
  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  // Handle form submission for community settings
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      if (!adminGroup || !adminGroup.id) {
        throw new Error('Community information not found');
      }
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('isPublic', formData.isPublic ? 'true' : 'false');
      
      if (logoFile) {
        try {
          const logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(logoFile);
          });
          
          formDataToSend.append('logoFile', logoBase64);
        } catch (fileError) {
          console.error('Error processing logo file:', fileError);
          setError('Failed to process logo image. Please try a different file.');
          setSaving(false);
          return;
        }
      }
      
      await updateAdminGroup(adminGroup.id, formDataToSend);
      
      setSuccessMessage('Community settings updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error updating community settings:', err);
      setError('Failed to update community settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle password change submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('New password and confirmation do not match');
      }
      
      if (formData.newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      
      await authService.updatePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      
      setFormData(prevState => ({
        ...prevState,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      setSuccessMessage('Password updated successfully');
      setShowPasswordModal(false);
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle name editing
  const handleNameEdit = () => {
    setIsEditingName(true);
    setEditedName(currentUser?.name || '');
  };

  const handleNameSave = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await authService.updateProfile({
        name: editedName
      });

      // Refresh the user profile in AuthContext to update sidebar
      await refreshUserProfile();

      // Update local component state
      setProfile(prev => ({ ...prev, name: editedName }));
      setFormData(prev => ({ ...prev, userName: editedName }));
      
      setSuccessMessage('Name updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error updating name:', err);
      setError('Failed to update name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
    setEditedName(currentUser?.name || '');
  };

  // Handle logout
  const handleLogout = () => {
    // Immediately clear all auth data and redirect
    localStorage.clear();
    window.location.replace('/login');
  };

  if (groupLoading) {
    return (
      <div className="admin-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile information...</p>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title="Account Settings"
      icon={<FontAwesomeIcon icon={faCog} />}
      subtitle="Manage your community and account preferences"
      loading={false}
      error={error}
    >
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          <div className="success-content">
            <FontAwesomeIcon icon={faCheck} />
            {successMessage}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="simple-tabs">
        <button 
          className={`simple-tab ${activeTab === 'account' ? 'active' : ''}`} 
          onClick={() => setActiveTab('account')}
        >
          Account
        </button>

      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="account-tab-content">
            {/* Left Side - Community Settings */}
            <div className="community-settings-section">
              <h2>Community Settings</h2>
              <form onSubmit={handleGroupSubmit} className="profile-form">
              {/* Community Logo */}
              <div className="logo-section">
                <div className="logo-upload-container">
                  <div 
                    className="logo-upload" 
                    onClick={handleLogoClick}
                    style={{ backgroundImage: logoPreview ? `url(${logoPreview})` : 'none' }}
                  >
                    {!logoPreview && (
                      <div className="logo-placeholder">
                        <FontAwesomeIcon icon={faCamera} size="2x" />
                        <span>Upload Logo</span>
                      </div>
                    )}
                  </div>
                  <input 
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    className="file-input"
                  />
                  <div className="logo-info">
                    <p className="logo-hint">Click to upload a new logo</p>
                    <p className="logo-requirements">
                      Recommended: 300×300px, max 5MB (JPEG, PNG, GIF)
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="name">Community Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter community name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Community Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Enter community description"
                  className="form-textarea"
                ></textarea>
                <button 
                  type="submit" 
                  className="save-button"
                  disabled={saving}
                  style={{ marginTop: '12px' }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
            </div>

            {/* Right Side - Admin Profile */}
            <div className="admin-profile-section">
              <h2>Admin Profile</h2>
              
              <div className="form-group">
                <label htmlFor="userName">Name</label>
                <input
                  type="text"
                  id="userName"
                  name="userName"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Enter your name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="userEmail">Email</label>
                <input
                  type="email"
                  id="userEmail"
                  value={currentUser?.email || ''}
                  placeholder="Email address"
                  className="form-input"
                  disabled
                />
                <div className="field-actions">
                  <button 
                    className="reset-password-link"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    <FontAwesomeIcon icon={faLock} />
                    Reset my password
                  </button>
                </div>
                <button 
                  type="button"
                  onClick={handleNameSave}
                  className="save-button"
                  disabled={saving}
                  style={{ marginTop: '12px' }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}




      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowPasswordModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Reset Password</h3>
              <button 
                className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            
            {/* Modal Form */}
            <form onSubmit={handlePasswordSubmit} className="p-6 space-y-5">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password *
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  placeholder="Enter current password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password *
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  required
                  placeholder="Enter new password (min. 6 characters)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving}
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default AdminProfilePage;